/**
 * AdminPanel - cross-tenant super-admin views (gated server-side + by useAdminAccess).
 * Tabs: Overview (platform totals), Tenants (all tenants), AI Usage (agent cost rollup).
 */
import React, { useState } from 'react';
import { ShieldCheck, Building2, Bot, Loader2, AlertTriangle } from 'lucide-react';
import { Card, Badge } from './ui';
import { useAdminAccess, useAdminOverview, useAdminTenants, useAdminAiUsage, useSetAgentConfig } from '../hooks/useAdmin';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'tenants', label: 'Tenants' },
  { id: 'ai-usage', label: 'AI Usage' },
];

function Stat({ label, value }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-grey-500 dark:text-grey-400">{label}</div>
      <div className="text-2xl font-semibold text-grey-900 dark:text-grey-50 mt-1">{value}</div>
    </Card>
  );
}

function OverviewTab() {
  const { data, isLoading, isError } = useAdminOverview();
  if (isLoading) return <Loading />;
  if (isError || !data) return <ErrorBox />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <Stat label="Tenants" value={data.tenants ?? 0} />
      <Stat label="Applicants" value={data.applicants ?? 0} />
      <Stat label="AI reviews (this month)" value={data.ai_agent_runs_this_month ?? 0} />
      <Stat label="AI cost (this month)" value={`$${((data.ai_agent_cost_cents_this_month ?? 0) / 100).toFixed(2)}`} />
    </div>
  );
}

function Toggle({ on, pending, onClick, labelOn = 'On', labelOff = 'Off' }) {
  return (
    <button
      onClick={onClick}
      disabled={pending}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
        on
          ? 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30'
          : 'bg-grey-100 dark:bg-grey-800 text-grey-500 border-grey-200 dark:border-grey-700'
      } ${pending ? 'opacity-50' : 'hover:border-teal-500'}`}
    >
      {pending ? '…' : on ? labelOn : labelOff}
    </button>
  );
}

function TenantsTab() {
  const { data, isLoading, isError } = useAdminTenants();
  const setConfig = useSetAgentConfig();
  if (isLoading) return <Loading />;
  if (isError || !data) return <ErrorBox />;
  const tenants = data.tenants || [];
  const pendingId = setConfig.isPending ? setConfig.variables?.tenant_id : null;
  return (
    <Card>
      <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex items-center gap-2">
        <Building2 size={15} className="text-grey-500" />
        <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">All tenants ({tenants.length})</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-grey-500 bg-grey-50 dark:bg-grey-800/40">
              <th className="px-4 py-2 font-semibold">Tenant</th>
              <th className="px-4 py-2 font-semibold">Plan</th>
              <th className="px-4 py-2 font-semibold">Applicants</th>
              <th className="px-4 py-2 font-semibold">Auto-approve</th>
              <th className="px-4 py-2 font-semibold">Scheduled review</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => {
              const agent = t.agent || {};
              const autoApprove = !!agent.auto_approve;
              const scheduled = !!(agent.scheduled_review && agent.scheduled_review.enabled);
              const busy = pendingId === t.id;
              return (
                <tr key={t.id} className="border-t border-grey-200 dark:border-grey-800">
                  <td className="px-4 py-2 font-medium text-grey-900 dark:text-grey-50">{t.name || t.id}</td>
                  <td className="px-4 py-2"><Badge variant="default">{t.plan_tier || '—'}</Badge></td>
                  <td className="px-4 py-2 text-grey-600 dark:text-grey-300">{t.applicants ?? 0}</td>
                  <td className="px-4 py-2">
                    <Toggle
                      on={autoApprove}
                      pending={busy}
                      onClick={() => setConfig.mutate({ tenant_id: t.id, auto_approve: !autoApprove })}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Toggle
                      on={scheduled}
                      pending={busy}
                      onClick={() => setConfig.mutate({ tenant_id: t.id, scheduled_review_enabled: !scheduled })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11px] text-grey-400 border-t border-grey-200 dark:border-grey-800">
        Auto-approve: agent auto-approves low-risk applicants (off = recommend-only).
        Scheduled review: agent reviews new applicants hourly on the worker.
      </div>
    </Card>
  );
}

function AiUsageTab() {
  const { data, isLoading, isError } = useAdminAiUsage();
  if (isLoading) return <Loading />;
  if (isError || !data) return <ErrorBox />;
  const rows = data.by_tenant || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Stat label="Total agent runs" value={data.total_runs ?? 0} />
        <Stat label="Total AI cost" value={`$${(data.total_cost_dollars ?? 0).toFixed(2)}`} />
        <Stat label="Tenants using the agent" value={rows.length} />
      </div>
      <Card>
        <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex items-center gap-2">
          <Bot size={15} className="text-teal-500" />
          <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">AI agent usage by tenant</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-grey-500 bg-grey-50 dark:bg-grey-800/40">
                <th className="px-4 py-2 font-semibold">Tenant</th>
                <th className="px-4 py-2 font-semibold">Agent runs</th>
                <th className="px-4 py-2 font-semibold">Cost</th>
                <th className="px-4 py-2 font-semibold">Last run</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-grey-500">No agent usage yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.tenant_id} className="border-t border-grey-200 dark:border-grey-800">
                  <td className="px-4 py-2 font-medium text-grey-900 dark:text-grey-50">{r.tenant_name || r.tenant_id}</td>
                  <td className="px-4 py-2 text-grey-600 dark:text-grey-300">{r.agent_runs}</td>
                  <td className="px-4 py-2 text-grey-600 dark:text-grey-300">${(r.cost_dollars ?? 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-grey-500">{r.last_run_at ? new Date(r.last_run_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-2 text-grey-500 text-sm py-8">
      <Loader2 size={16} className="animate-spin" /> Loading…
    </div>
  );
}

function ErrorBox() {
  return (
    <div className="flex items-center gap-2 text-red-500 text-sm py-8">
      <AlertTriangle size={16} /> Failed to load.
    </div>
  );
}

export default function AdminPanel() {
  const { data: access, isLoading } = useAdminAccess();
  const [tab, setTab] = useState('overview');

  if (isLoading) return <div className="p-6"><Loading /></div>;
  if (!access?.is_super_admin) {
    return (
      <div className="max-w-[600px] mx-auto p-10 text-center text-grey-500">
        <ShieldCheck size={28} className="mx-auto mb-3 text-grey-400" />
        This area is restricted to platform administrators.
      </div>
    );
  }

  return (
    <div className="max-w-[1000px] mx-auto px-2 py-2">
      <div className="flex items-center gap-2 mb-5">
        <ShieldCheck size={22} className="text-teal-500" />
        <h1 className="text-[24px] font-semibold tracking-tight text-grey-900 dark:text-grey-50">Admin</h1>
        <Badge variant="info">Cross-tenant</Badge>
      </div>

      <div className="flex gap-1 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-xl p-1 mb-6 w-fit">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id
                ? 'bg-teal-500/10 text-teal-500'
                : 'text-grey-500 hover:text-grey-900 dark:hover:text-grey-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'tenants' && <TenantsTab />}
      {tab === 'ai-usage' && <AiUsageTab />}
    </div>
  );
}
